/**
 * Which requests are being refused as busy right now (#392), for a state that
 * says so. This file is the plumbing: nothing here is drawn, and what is drawn
 * and where is `components/ui/BusyLine.tsx` (owner ruling CAP-O26, option B).
 *
 * `client.ts` opens a chain for each read it may send again, tells it about
 * every busy answer, and ends it when the request is answered or given up. The
 * answer to "is it busy?" is yes when either of two things holds:
 *
 * 1. **One request has been refused as busy `BUSY_RETRY.repeatedAfter` times and
 *    is still going.** It is the request, not the app, that fails repeatedly: a
 *    counter of consecutive busy answers across the app would flip to zero at
 *    every request a struggling server still answers and never reach two.
 * 2. **The streak is armed and unbroken.** A request refused repeatedly *arms*
 *    it, and it then stands after that request is given up, so the next surface
 *    to load does not start from silence the server has not earned: the page's
 *    gate (`/api/me`) gives up after about 13 s and the panels it lets through
 *    begin at once, and without the streak the line would vanish at the give-up
 *    and come back six seconds later. "Stays until the next success", as the
 *    ruling says. Any answer that is not a busy 503 breaks it (a 404 and a 401
 *    are answers: the server is up), a write's included; and it lapses by itself
 *    `BUSY_RETRY.streakTtlS` after the last busy answer, so a Try again tapped
 *    minutes later starts clean. What arms it is one request's repeated
 *    refusals, never several requests refused once together: a page that fires
 *    ten queries into one busy moment has had one busy moment, not ten.
 *    (Counting answers across requests lit the line at the first round of a
 *    parallel page load, about 4 s earlier than "repeated" means.)
 *
 * The shape suits `useSyncExternalStore(subscribeBusy, getBusySnapshot)`.
 */

import { BUSY_RETRY } from './busyRetry'

export interface BusySnapshot {
  readonly busy: boolean
  /** The paths of the requests that are struggling, each once, in the order they became so. */
  readonly paths: readonly string[]
}

export interface BusyChain {
  /** The server answered this request with a busy 503. */
  answeredBusy(): void
  /** The server answered this request with anything else: it is up. */
  answered(): void
  /** The request is over: answered, given up, or aborted. Safe to call twice. */
  end(): void
}

const QUIET: BusySnapshot = Object.freeze({ busy: false, paths: Object.freeze([]) as readonly string[] })

interface Chain {
  path: string
  answers: number
}

const chains = new Set<Chain>()
const listeners = new Set<() => void>()
let snapshot: BusySnapshot = QUIET
let armed = false
let lastBusyAt = 0

function struggling(): Chain[] {
  return [...chains].filter((chain) => chain.answers >= BUSY_RETRY.repeatedAfter)
}

/** Recompute, and tell the subscribers only if what they can read changed. */
function refresh(): void {
  const paths = [...new Set(struggling().map((chain) => chain.path))]
  const busy = paths.length > 0 || armed
  const unchanged =
    busy === snapshot.busy &&
    paths.length === snapshot.paths.length &&
    paths.every((path, i) => path === snapshot.paths[i])
  if (unchanged) return
  snapshot = busy ? { busy: true, paths } : QUIET
  for (const listener of [...listeners]) listener()
}

/** A streak the last busy answer left long ago is over, whoever has not noticed yet. */
function lapseStaleStreak(): void {
  if (armed && Date.now() - lastBusyAt > BUSY_RETRY.streakTtlS * 1000) {
    armed = false
    refresh()
  }
}

export function startBusyChain(path: string): BusyChain {
  lapseStaleStreak()
  const chain: Chain = { path, answers: 0 }
  chains.add(chain)
  return {
    answeredBusy() {
      chain.answers += 1
      lastBusyAt = Date.now()
      if (chain.answers >= BUSY_RETRY.repeatedAfter) armed = true
      refresh()
    },
    answered() {
      noteAnswered()
    },
    end() {
      if (chains.delete(chain)) refresh()
    },
  }
}

/**
 * An answer that is not a busy 503 reached the app: the server is up, so the
 * streak is over. Called for a write's answer too, which has no chain.
 */
export function noteAnswered(): void {
  if (!armed) return
  armed = false
  refresh()
}

export function getBusySnapshot(): BusySnapshot {
  return snapshot
}

export function subscribeBusy(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
